import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { QRCodeSVG } from "qrcode.react";
import {
  MessageCircle, Send, Save, RefreshCw, Loader2, Info, Clock,
  LogIn, LogOut, AlarmClock, Hourglass, CalendarX,
  UserPlus, QrCode, Printer, ExternalLink,
  Smartphone, Wifi, WifiOff, Link2,
} from "lucide-react";

const EVENT_TOGGLES = [
  { key: "notify_clock_in", label: "出勤打刻", desc: "出勤を記録したとき", icon: LogIn },
  { key: "notify_clock_out", label: "退勤打刻", desc: "退勤を記録したとき（労働・残業時間つき）", icon: LogOut },
  { key: "notify_late", label: "遅刻", desc: "始業時刻＋猶予を過ぎて出勤したとき", icon: AlarmClock },
  { key: "notify_overtime", label: "残業", desc: "退勤時に残業が発生したとき", icon: Hourglass },
  { key: "notify_missing_out", label: "退勤打刻漏れ", desc: "毎日深夜1時に前日分を自動チェック", icon: CalendarX },
];

const TYPE_LABEL = {
  clock_in: "出勤", clock_out: "退勤", late: "遅刻", overtime: "残業",
  missing_out: "打刻漏れ", test: "テスト",
};
const RECIPIENT_LABEL = { employee: "本人", admin: "運営", test: "テスト" };
const STATUS_STYLE = {
  sent: "bg-emerald-600 text-white",
  mocked: "bg-amber-500 text-white",
  failed: "bg-red-600 text-white",
};

export default function WhatsAppSettingsPage() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [adminNumbersText, setAdminNumbersText] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [testNumber, setTestNumber] = useState("");
  const [testMessage, setTestMessage] = useState("");
  const [testing, setTesting] = useState(false);
  const [companyCode, setCompanyCode] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [regStatus, setRegStatus] = useState(null);
  const [receiver, setReceiver] = useState(null);
  const [recvQr, setRecvQr] = useState(null);
  const [recvConnecting, setRecvConnecting] = useState(false);
  const [recvDisconnecting, setRecvDisconnecting] = useState(false);
  const recvPollRef = useRef(null);

  const loadSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/whatsapp/settings`);
      setSettings(res.data);
      setAdminNumbersText((res.data.admin_numbers || []).join("\n"));
    } catch (e) {
      toast.error("設定の読み込みに失敗しました");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const res = await axios.get(`${API}/whatsapp/logs?limit=50`);
      setLogs(res.data || []);
    } catch (e) {
      // ignore
    } finally {
      setLogsLoading(false);
    }
  }, []);

  const loadMeta = useCallback(async () => {
    try {
      const [meRes, statusRes] = await Promise.all([
        axios.get(`${API}/auth/me`),
        axios.get(`${API}/whatsapp/registration-status`),
      ]);
      setCompanyCode(meRes.data?.company?.code || "");
      setCompanyName(meRes.data?.company?.name || "");
      setRegStatus(statusRes.data);
    } catch (e) {
      // ignore
    }
  }, []);

  const loadReceiver = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/whatsapp/receiver/status`);
      setReceiver(res.data);
      return res.data;
    } catch (e) {
      return null;
    }
  }, []);

  const pollReceiver = useCallback(() => {
    if (recvPollRef.current) clearInterval(recvPollRef.current);
    recvPollRef.current = setInterval(async () => {
      try {
        const st = await loadReceiver();
        if (st && st.service === "connected") {
          setRecvQr(null);
          setRecvConnecting(false);
          clearInterval(recvPollRef.current);
          toast.success("受信用WhatsAppが接続されました");
        } else {
          const qr = await axios.get(`${API}/whatsapp/receiver/qr`);
          if (qr.data?.qr) setRecvQr(qr.data.qr);
        }
      } catch (e) { /* keep polling */ }
    }, 3000);
  }, [loadReceiver]);

  const handleConnectReceiver = async () => {
    setRecvConnecting(true);
    try {
      await axios.post(`${API}/whatsapp/receiver/connect`);
      toast.message("接続を開始しました。表示されるQRを登録用の端末で読み取ってください。");
      pollReceiver();
    } catch (e) {
      toast.error(e.response?.data?.detail || "受信サービスに接続できません");
      setRecvConnecting(false);
    }
  };

  const handleDisconnectReceiver = async () => {
    setRecvDisconnecting(true);
    try {
      await axios.post(`${API}/whatsapp/receiver/disconnect`);
      if (recvPollRef.current) clearInterval(recvPollRef.current);
      setRecvQr(null);
      setRecvConnecting(false);
      toast.success("WhatsAppからログアウトしました。「接続する」で別の番号を再登録できます。");
      setTimeout(loadReceiver, 800);
    } catch (e) {
      toast.error(e.response?.data?.detail || "ログアウトに失敗しました");
    } finally {
      setRecvDisconnecting(false);
    }
  };

  useEffect(() => {
    loadSettings();
    loadLogs();
    loadMeta();
    loadReceiver();
    return () => { if (recvPollRef.current) clearInterval(recvPollRef.current); };
  }, [loadSettings, loadLogs, loadMeta, loadReceiver]);

  const setField = (key, value) => setSettings((s) => ({ ...s, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: settings.enabled,
        notify_employee: settings.notify_employee,
        notify_admin: settings.notify_admin,
        notify_clock_in: settings.notify_clock_in,
        notify_clock_out: settings.notify_clock_out,
        notify_late: settings.notify_late,
        notify_overtime: settings.notify_overtime,
        notify_missing_out: settings.notify_missing_out,
        admin_numbers: adminNumbersText,
        work_start_time: settings.work_start_time,
        late_grace_minutes: Number(settings.late_grace_minutes) || 0,
        default_country_code: settings.default_country_code,
      };
      const res = await axios.put(`${API}/whatsapp/settings`, payload);
      setSettings((s) => ({ ...res.data, send_configured: s.send_configured }));
      setAdminNumbersText((res.data.admin_numbers || []).join("\n"));
      toast.success("設定を保存しました");
    } catch (e) {
      toast.error("保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testNumber.trim()) {
      toast.error("送信先番号を入力してください");
      return;
    }
    setTesting(true);
    try {
      const res = await axios.post(`${API}/whatsapp/test`, {
        number: testNumber,
        message: testMessage || undefined,
      });
      if (res.data.mocked) {
        toast.warning(`モック送信（未接続）: ${res.data.to}`);
      } else if (res.data.ok) {
        toast.success(`送信しました: ${res.data.to}`);
      } else {
        toast.error(`送信失敗: ${res.data.error || "不明なエラー"}`);
      }
      loadLogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "テスト送信に失敗しました");
    } finally {
      setTesting(false);
    }
  };

  const registerUrl = companyCode ? `${window.location.origin}/wa-register?company=${companyCode}` : "";

  const printRegisterQr = async () => {
    if (!registerUrl) return;
    const QRCode = await import("qrcode");
    QRCode.toDataURL(registerUrl, { width: 340, margin: 2 }, (err, url) => {
      if (err) { toast.error("QRコードの生成に失敗しました"); return; }
      const w = window.open("", "_blank");
      if (!w) return;
      w.document.write(`
        <html><head><title>WhatsApp通知 登録用QRコード</title>
        <style>
          body{font-family:sans-serif;text-align:center;padding:40px;color:#111}
          h1{font-size:22px;margin-bottom:4px}
          p{color:#444;margin:6px 0}
          .qr{margin:24px auto}
          ol{text-align:left;display:inline-block;color:#333;font-size:15px;line-height:1.9}
          .url{font-size:12px;color:#777;word-break:break-all;margin-top:16px}
        </style></head>
        <body>
          <h1>WhatsApp通知の登録</h1>
          <p>このQRコードをスマホのカメラで読み取ってください</p>
          <div class="qr"><img src="${url}" width="300" height="300"/></div>
          <ol>
            <li>スマホのカメラでこのQRコードを読み取る</li>
            <li>開いた画面で、自分の<b>タイムカードのQRコード</b>をかざす</li>
            <li>自分の<b>WhatsApp番号</b>を入力して「登録」を押す</li>
          </ol>
          <p class="url">${registerUrl}</p>
          <script>window.onload=function(){window.print();}</script>
        </body></html>`);
      w.document.close();
    });
  };

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="whatsapp-loading">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="whatsapp-settings-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-xl bg-emerald-600 flex items-center justify-center">
            <MessageCircle className="w-6 h-6 text-white" />
          </span>
          <div>
            <h1 className="text-xl font-bold">WhatsApp通知</h1>
            <p className="text-sm text-slate-400">打刻や勤怠アラートをWhatsAppへ自動送信します</p>
          </div>
        </div>
        <div className="flex items-center gap-3 bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5">
          <div className="text-right">
            <p className="text-xs text-slate-400">通知全体</p>
            <p className="text-sm font-semibold">{settings.enabled ? "有効" : "停止中"}</p>
          </div>
          <Switch
            checked={!!settings.enabled}
            onCheckedChange={(v) => setField("enabled", v)}
            data-testid="whatsapp-enabled-switch"
          />
        </div>
      </div>

      {/* Connection notice */}
      {!settings.send_configured && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3" data-testid="whatsapp-mock-notice">
          <Info className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-200">
            PRCタイムのWhatsApp（自動受信・送信サービス）が未接続のため、現在は<b>モック動作</b>です。メッセージは実送信されず履歴に「モック」として記録されます。
            下の<b>「自動受信（登録用WhatsApp）」</b>でWhatsAppを接続すると、そのまま実送信へ切り替わります。
          </p>
        </div>
      )}

      {/* 配信者・通知内容の説明 */}
      <div className="rounded-xl border border-slate-700 bg-slate-800/60 px-4 py-4" data-testid="whatsapp-sender-info">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
          <div className="space-y-3 text-sm">
            <div>
              <p className="text-slate-400 text-xs mb-1">配信者（送信元）</p>
              <p className="text-slate-200 leading-relaxed">
                この通知は <b className="text-emerald-400">「{companyName || "会社名"}」</b> として配信されます。各メッセージの先頭に <span className="font-mono text-emerald-300">【{companyName || "会社名"}】</span> が付くので、受け取ったスタッフは<b>どこからの通知か</b>すぐに分かります。
              </p>
            </div>
            <div>
              <p className="text-slate-400 text-xs mb-1.5">通知内容（送られるお知らせ）</p>
              <div className="flex flex-wrap gap-1.5" data-testid="whatsapp-notify-contents">
                {["出勤打刻", "退勤打刻", "遅刻", "残業", "退勤打刻漏れ", "WhatsApp番号の登録完了"].map((t) => (
                  <span key={t} className="px-2 py-0.5 rounded-md bg-slate-700/70 text-xs text-slate-200">{t}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 通知する内容 */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader><CardTitle className="text-base">通知する内容</CardTitle></CardHeader>
        <CardContent className="space-y-1">
          {EVENT_TOGGLES.map(({ key, label, desc, icon: Icon }) => (
            <div key={key} className="flex items-center justify-between py-2.5 border-b border-slate-700/60 last:border-0">
              <div className="flex items-center gap-3">
                <Icon className="w-5 h-5 text-emerald-400" />
                <div>
                  <p className="text-sm font-medium">{label}</p>
                  <p className="text-xs text-slate-400">{desc}</p>
                </div>
              </div>
              <Switch
                checked={!!settings[key]}
                onCheckedChange={(v) => setField(key, v)}
                data-testid={`whatsapp-toggle-${key}`}
              />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* 送信先 */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader><CardTitle className="text-base">送信先</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">従業員本人に送る</p>
              <p className="text-xs text-slate-400">各従業員の「WhatsApp番号」を宛先に使用します（国番号は自動補完）</p>
            </div>
            <Switch
              checked={!!settings.notify_employee}
              onCheckedChange={(v) => setField("notify_employee", v)}
              data-testid="whatsapp-toggle-notify_employee"
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">運営（管理者）にも送る</p>
              <p className="text-xs text-slate-400">下に登録した番号へまとめて通知します</p>
            </div>
            <Switch
              checked={!!settings.notify_admin}
              onCheckedChange={(v) => setField("notify_admin", v)}
              data-testid="whatsapp-toggle-notify_admin"
            />
          </div>
          <div>
            <Label className="text-sm">運営の受信番号（1行に1件、またはカンマ区切り）</Label>
            <Textarea
              value={adminNumbersText}
              onChange={(e) => setAdminNumbersText(e.target.value)}
              placeholder={"09012345678\n+819087654321"}
              rows={3}
              className="mt-1.5 bg-slate-700 border-slate-600 font-mono text-sm"
              data-testid="whatsapp-admin-numbers"
            />
          </div>
        </CardContent>
      </Card>

      {/* スタッフ番号の登録 */}
      <Card className="bg-slate-800 border-slate-700" data-testid="whatsapp-register-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-emerald-400" />スタッフ番号の登録
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-300">
            通知の宛先には、従業員の<b className="text-emerald-400">「WhatsApp番号」</b>を使います（電話番号とは別管理）。登録方法は2通りです。
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="rounded-xl border border-slate-700 bg-slate-900/40 p-4 space-y-1.5">
              <p className="text-sm font-semibold text-emerald-400">① 管理者が入力</p>
              <p className="text-xs text-slate-400">「従業員」ページで各スタッフを開き、「WhatsApp番号」欄に入力して保存します。</p>
            </div>
            <div className="rounded-xl border border-slate-700 bg-slate-900/40 p-4 space-y-1.5">
              <p className="text-sm font-semibold text-emerald-400">② スタッフ本人が登録</p>
              <p className="text-xs text-slate-400">下のQRを掲示 → 本人のタイムカードQRをかざす →「WhatsAppで登録」を押して送信するだけ（番号の手入力も可）。</p>
            </div>
          </div>

          {registerUrl ? (
            <div className="flex flex-col sm:flex-row items-center gap-4 rounded-xl border border-slate-200 bg-white p-4">
              <div className="bg-white p-1 rounded-lg" data-testid="whatsapp-register-qr">
                <QRCodeSVG value={registerUrl} size={128} level="M" />
              </div>
              <div className="flex-1 w-full space-y-2 text-center sm:text-left">
                <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5 justify-center sm:justify-start">
                  <QrCode className="w-4 h-4" /> スタッフ登録用QRコード
                </p>
                <p className="text-xs text-slate-500 break-all">{registerUrl}</p>
                <div className="flex gap-2 justify-center sm:justify-start">
                  <Button size="sm" variant="outline" onClick={() => window.open(registerUrl, "_blank")} className="border-slate-300 text-slate-700 hover:bg-slate-100" data-testid="whatsapp-register-open">
                    <ExternalLink className="w-4 h-4 mr-1.5" /> 開く
                  </Button>
                  <Button size="sm" onClick={printRegisterQr} className="bg-emerald-600 hover:bg-emerald-700" data-testid="whatsapp-register-print">
                    <Printer className="w-4 h-4 mr-1.5" /> 印刷して掲示
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-slate-700 bg-slate-900/40 p-4 text-sm text-slate-400">企業コードを読み込み中です…</div>
          )}

          {regStatus && (
            <div className="flex items-center justify-between rounded-xl bg-slate-900/50 border border-slate-700 px-4 py-3" data-testid="whatsapp-register-status">
              <span className="text-sm text-slate-300">WhatsApp番号 登録済み</span>
              <span className="text-sm font-semibold">
                <span className="text-emerald-400">{regStatus.registered}</span>
                <span className="text-slate-500"> / {regStatus.total} 名</span>
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 自動受信（登録用WhatsApp） */}
      <Card className="bg-slate-800 border-slate-700" data-testid="whatsapp-receiver-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Smartphone className="w-4 h-4 text-emerald-400" />自動受信（登録用WhatsApp）
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-300">
            スタッフが登録画面の「WhatsAppで登録」から<b className="text-emerald-400">メッセージを送るだけ</b>で番号が自動登録されます。これを使うには、受信用のWhatsApp番号をここで<b>1台だけ接続</b>し、その端末を常時オンラインにしてください。
          </p>
          <div className="flex items-center justify-between rounded-xl bg-slate-900/50 border border-slate-700 px-4 py-3" data-testid="whatsapp-receiver-status">
            <div className="flex items-center gap-2">
              {receiver?.service === "connected"
                ? <Wifi className="w-5 h-5 text-emerald-400" />
                : <WifiOff className="w-5 h-5 text-slate-500" />}
              <div>
                <p className="text-sm font-medium">
                  {receiver?.service === "connected" ? "接続中"
                    : receiver?.service === "qr_ready" ? "QR待ち（読み取ってください）"
                    : receiver?.service === "unavailable" ? "受信サービス停止中"
                    : "未接続"}
                </p>
                {receiver?.masked_receiver && <p className="text-xs text-slate-400 font-mono">{receiver.masked_receiver}</p>}
              </div>
            </div>
            {receiver?.service !== "connected" && (
              <Button
                size="sm"
                onClick={handleConnectReceiver}
                disabled={recvConnecting || receiver?.service === "unavailable"}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="whatsapp-receiver-connect"
              >
                {recvConnecting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Link2 className="w-4 h-4 mr-1.5" />}
                接続する
              </Button>
            )}
            {receiver?.service === "connected" && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleDisconnectReceiver}
                disabled={recvDisconnecting}
                className="border-red-500/60 text-red-300 hover:bg-red-500/10"
                data-testid="whatsapp-receiver-logout"
              >
                {recvDisconnecting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <LogOut className="w-4 h-4 mr-1.5" />}
                ログアウト
              </Button>
            )}
          </div>

          {recvQr && receiver?.service !== "connected" && (
            <div className="flex flex-col items-center gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid="whatsapp-receiver-qr">
              <img src={recvQr} alt="WhatsApp Link QR" width={220} height={220} />
              <p className="text-xs text-slate-600 text-center">
                登録用のスマホで <b>WhatsApp → 設定 → リンク済み端末 → 端末をリンク</b> から読み取ってください
              </p>
            </div>
          )}

          {receiver && receiver.secret_configured === false && (
            <p className="text-xs text-amber-300">※ サーバー側の受信シークレット（WA_INBOUND_SECRET）が未設定です。設定後に自動受信が有効になります。</p>
          )}
          {receiver?.service === "unavailable" && (
            <p className="text-xs text-slate-500">※ 受信サービス（8010）が起動していません。本番サーバーで起動後にご利用いただけます。</p>
          )}
        </CardContent>
      </Card>

      {/* 判定・既定値 */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader><CardTitle className="text-base">判定・既定値</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-sm flex items-center gap-1.5"><Clock className="w-4 h-4" />始業時刻</Label>
            <Input
              type="time"
              value={settings.work_start_time || "09:00"}
              onChange={(e) => setField("work_start_time", e.target.value)}
              className="mt-1.5 bg-slate-700 border-slate-600"
              data-testid="whatsapp-work-start"
            />
          </div>
          <div>
            <Label className="text-sm">遅刻の猶予（分）</Label>
            <Input
              type="number" min="0"
              value={settings.late_grace_minutes ?? 5}
              onChange={(e) => setField("late_grace_minutes", e.target.value)}
              className="mt-1.5 bg-slate-700 border-slate-600"
              data-testid="whatsapp-grace"
            />
          </div>
          <div>
            <Label className="text-sm">既定の国番号</Label>
            <Input
              value={settings.default_country_code || "81"}
              onChange={(e) => setField("default_country_code", e.target.value)}
              className="mt-1.5 bg-slate-700 border-slate-600"
              data-testid="whatsapp-country-code"
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="whatsapp-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          設定を保存
        </Button>
      </div>

      {/* テスト送信 */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader><CardTitle className="text-base">テスト送信</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">送信先番号</Label>
              <Input
                value={testNumber}
                onChange={(e) => setTestNumber(e.target.value)}
                placeholder="09011112222 または +819011112222"
                className="mt-1.5 bg-slate-700 border-slate-600"
                data-testid="whatsapp-test-number"
              />
            </div>
            <div>
              <Label className="text-sm">本文（任意）</Label>
              <Input
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                placeholder="【PRC勤怠】テスト送信です。"
                className="mt-1.5 bg-slate-700 border-slate-600"
                data-testid="whatsapp-test-message"
              />
            </div>
          </div>
          <Button onClick={handleTest} disabled={testing} variant="outline" className="border-emerald-600 text-emerald-400 hover:bg-emerald-600/10" data-testid="whatsapp-test-btn">
            {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            テスト送信
          </Button>
        </CardContent>
      </Card>

      {/* 送信履歴 */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">送信履歴（最新50件）</CardTitle>
          <Button size="sm" variant="ghost" onClick={loadLogs} disabled={logsLoading} data-testid="whatsapp-logs-refresh">
            <RefreshCw className={`w-4 h-4 ${logsLoading ? "animate-spin" : ""}`} />
          </Button>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center" data-testid="whatsapp-logs-empty">まだ送信履歴はありません</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="whatsapp-logs-table">
                <thead>
                  <tr className="text-left text-xs text-slate-400 border-b border-slate-700">
                    <th className="py-2 pr-3 whitespace-nowrap">日時</th>
                    <th className="py-2 pr-3 whitespace-nowrap">種別</th>
                    <th className="py-2 pr-3 whitespace-nowrap">宛先</th>
                    <th className="py-2 pr-3 whitespace-nowrap">番号</th>
                    <th className="py-2 pr-3 whitespace-nowrap">状態</th>
                    <th className="py-2 pr-3">本文</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l) => (
                    <tr key={l.id} className="border-b border-slate-700/50 align-top">
                      <td className="py-2 pr-3 whitespace-nowrap text-xs text-slate-400">
                        {new Date(l.created_at).toLocaleString("ja-JP", { timeZone: "Asia/Tokyo", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })}
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap">{TYPE_LABEL[l.type] || l.type}</td>
                      <td className="py-2 pr-3 whitespace-nowrap text-slate-300">{RECIPIENT_LABEL[l.recipient_type] || l.recipient_type}</td>
                      <td className="py-2 pr-3 whitespace-nowrap font-mono text-xs">{l.to}</td>
                      <td className="py-2 pr-3 whitespace-nowrap">
                        <Badge className={STATUS_STYLE[l.status] || "bg-slate-600"}>
                          {l.status === "mocked" ? "モック" : l.status === "sent" ? "送信" : "失敗"}
                        </Badge>
                      </td>
                      <td className="py-2 pr-3 text-xs text-slate-300 min-w-[200px]">{l.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
